/* ––
 * ––– Imports
 * –––––––––––––––––––––––––––––––– */
import { useLazyQuery, useMutation } from '@apollo/client';
import { List, Map } from 'immutable';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, {
  useState, useMemo, useContext, useEffect, useReducer,
} from 'react';

import AutorenewIcon from '@mui/icons-material/Autorenew';
import LoadingButton from '@mui/lab/LoadingButton';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress, { circularProgressClasses } from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import {
  useTheme, styled, lighten, darken,
} from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { checkLocalStorage, getItem, setItem } from 'helpers/local_storage';
import CandidateContext from 'pages/LandingPage/CandidateContext';
import { APPOINTMENT_RECORD_CREATE } from 'queries/appointment/create';
import { APPOINTMENT_RECORD_DESTROY } from 'queries/appointment/destroy';
import { CALENDAR_SLOTS_FETCH } from 'queries/calendar/fetch';
import { CALENDAR_RECORD_LIMITED_AVAILABILITY_NOTIFICATION_SCHEDULE } from 'queries/calendar/schedule';
import recordJS from 'records';
import CandidateApplication from 'records/candidate_application';

import {
  BookingStage,
  BookingStageAction,
  BookingStageActionStatus,
  BookingStageStateAction,
  defaultBookingStageState,
  buildPendingStageAction,
  defaultStageActionsMap,
  bookingStageStateReducer,
} from './booking-stage-reducer';
import DatePicker from './DatePicker';
import AppointmentCard from './AppointmentCard';
import AppointmentSlotPicker from './AppointmentSlotPicker';
import AppointmentSlotVerifier from './AppointmentSlotVerifier';

/* ––
 * ––– Constants
 * –––––––––––––––––––––––––––––––– */
const DEFAULT_BOOKING_SLOT_DURATION = 60;
const DEFAULT_BOOKING_WINDOW_DAYS = 7;
const DEFAULT_BOOKING_WINDOW_EXTENSION_DAYS = 2;
const MIN_AVAILABLE_CALENDAR_SLOTS = 4;
const MAX_FAILED_CALENDAR_SLOTS_LOAD_ATTEMPTS = 1;
const MAX_BOOKING_WINDOW_EXTENSIONS = 5;
const DEFAULT_MINIMUM_BOOKING_NOTICE_VALUE = 0;
const DEFAULT_SNACKBAR_AUTO_HIDE_DURATION = 6500;

const DEFAULT_SLOT_DATE_FORMAT_OPTIONS = {
  weekday: 'long',
  month:   'long',
  day:     'numeric',
  year:    'numeric',
};

const BOOKING_TIMEZONE_STORAGE_KEY = 'booking-timezone';

const BookingDatasource = Object.freeze({
  RecruitmentCenter:         'recruitment_center',
  // Despite the name, this datasource now serves the round-robin pool of manager calendars,
  // not just the single assigned recruiter's calendar.
  AssignedRecruiterCalendar: 'assigned_recruiter_calendar',
});

const BookingNoticeUnit = Object.freeze({
  Minutes: 'minutes',
  Hours:   'hours',
  Days:    'days',
});

const CalendarType = Object.freeze({
  Regular:           'regular',
  RecruitmentCenter: 'recruitment_center',
});

const LoadingError = Object.freeze({
  BookingUnavailable:  'booking_unavailable',
  FailedCalendarFetch: 'failed_calendar_fetch',
  NoAvailableSlots:    'no_available_slots',
});

export const globalLoadingStages = new List([
  BookingStage.Initialization,
  BookingStage.NewAppointmentValidation,
  BookingStage.NewAppointmentSetup,
  BookingStage.ExistingAppointmentSetup,
  BookingStage.ExistingAppointmentDeletion,
]);

export const globalErrorStages = new List([
  BookingStage.NewAppointmentForbiddenError,
  BookingStage.NewAppointmentSetupError,
]);

const datasourceToCalendarTypeMap = Object.freeze({
  [BookingDatasource.RecruitmentCenter]:         CalendarType.RecruitmentCenter,
  [BookingDatasource.AssignedRecruiterCalendar]: CalendarType.Regular,
});

/* ––
 * ––– Helper Functions
 * –––––––––––––––––––––––––––––––– */
function getLocale() {
  return document.documentElement.lang;
}

function getTimeZones() {
  if (typeof Intl.supportedValuesOf !== 'undefined') {
    return Intl.supportedValuesOf('timeZone');
  }

  // Fallback to the current timezone
  const calculatedTimezone = DateTime.now().zoneName;

  if (calculatedTimezone) {
    return [calculatedTimezone];
  }

  return [];
}

function getDefaultTimezone(timeZonesData, campaign) {
  const cachedTimeZone = checkLocalStorage() ? getItem(BOOKING_TIMEZONE_STORAGE_KEY) : null;

  if (timeZonesData.includes(cachedTimeZone)) { return cachedTimeZone; }

  const systemTimeZone = DateTime.now().zoneName;

  if (timeZonesData.includes(systemTimeZone)) { return systemTimeZone; }

  const campaignTimeZone = campaign?.timezone;

  if (timeZonesData.includes(campaignTimeZone)) { return campaignTimeZone; }

  return timeZonesData[0];
}

function buildCalendarAvailability(rawSlots, timeZone, locale) {
  let calendarAvailability = new Map({});

  rawSlots.forEach((slot) => {
    const timeZonedSlotStart = DateTime.fromISO(slot.get('startTime'), { zone: timeZone }).setLocale(locale);
    const timeZonedSlotEnd = DateTime.fromISO(slot.get('endTime'), { zone: timeZone }).setLocale(locale);

    const dayId = timeZonedSlotStart.toISODate();

    const calendarSlot = new Map({
      id:         slot.get('id'),
      available:  slot.get('available'),
      startTime:  timeZonedSlotStart,
      endTime:    timeZonedSlotEnd,
      calendarId: slot.get('calendarId'),
    });

    if (!calendarAvailability.has(dayId)) {
      calendarAvailability = calendarAvailability
        .set(dayId, new Map({
          id: dayId, date: timeZonedSlotStart, available: false, slots: new List(),
        }));
    }

    const dayAvailability = calendarAvailability.get(dayId);

    const updatedDayAvailability = dayAvailability
      .set('slots', dayAvailability.get('slots').push(calendarSlot))
      .set('available', dayAvailability.get('available') || slot.get('available'));

    calendarAvailability = calendarAvailability.set(dayId, updatedDayAvailability);
  });

  return calendarAvailability.valueSeq().toList()
    .filter((dayAvailability) => dayAvailability.get('available'))
    .sort((dayA, dayB) => dayA.get('date').diff(dayB.get('date')).toMillis());
}

/* ––
 * ––– Inner Components
 * –––––––––––––––––––––––––––––––– */
const CircularProgressTrack = styled(CircularProgress)(({ theme }) => ({
  [`& .${circularProgressClasses.circle}`]: { color: lighten(theme.palette.primary.main, 0.9) },
}));

const RoundedCircularProgress = styled(CircularProgress)(() => ({
  [`& .${circularProgressClasses.circle}`]: { strokeLinecap: 'round' },
}));

/* ––
 * ––– Component
 * –––––––––––––––––––––––––––––––– */
function Booking({
  name = null,
  value = null,
  disabled = false,
  forced = false,
  onChange = null,
  onProcessing = null,
  onProcessingCompleted = null,
  application = null,
  datasource = null,
  disableReschedule = false,
}) {
  /* –– Resources
   * –––––––––––––––––––––––––––––––– */
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { campaign } = useContext(CandidateContext);

  const [bookingStageState, bookingStageDispatch] = useReducer(
    bookingStageStateReducer,
    defaultBookingStageState,
  );

  const currentStage = bookingStageState.get('currentStage');
  const pendingStageActions = bookingStageState.get('pendingStageActions');

  const [loadingError, setLoadingError] = useState(null);
  const [appliedBookingWindowExtensions, setAppliedBookingWindowExtensions] = useState(0);
  const [failedCalendarSlotsLoadAttempts, setFailedCalendarSlotsLoadAttempts] = useState(0);
  const [creationError, setCreationError] = useState(null);

  const [slotDuration, setSlotDuration] = useState(null);
  const [timeZone, setTimeZone] = useState(null);
  const timeZonesData = useMemo(() => getTimeZones(), []);
  const locale = useMemo(() => getLocale(), []);

  const [rawSlots, setRawSlots] = useState(null);
  const [calendarAvailability, setCalendarAvailability] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [appointment, setAppointment] = useState(null);

  const [
    fetchCalendarSlotsData,
    { data: calendarSlotsData, error: calendarSlotsDataError, loading: calendarSlotsDataLoading },
  ] = useLazyQuery(CALENDAR_SLOTS_FETCH);

  const [
    createAppointment,
    {
      loading: createAppointmentLoading,
      data: createAppointmentData,
      error: createAppointmentError,
    },
  ] = useMutation(APPOINTMENT_RECORD_CREATE);

  const [
    destroyAppointment,
    { data: destroyAppointmentData, error: destroyAppointmentError },
  ] = useMutation(APPOINTMENT_RECORD_DESTROY);

  const [
    scheduleLimitedCalendarAvailabilityNotification,
  ] = useMutation(CALENDAR_RECORD_LIMITED_AVAILABILITY_NOTIFICATION_SCHEDULE);

  /* –– Handlers
   * –––––––––––––––––––––––––––––––– */
  const resolveSelectBookingModeAction = () => {
    bookingStageDispatch({
      type:             BookingStageStateAction.CompleteStageAction,
      targetActionType: BookingStageAction.SelectBookingMode,
    });

    if (value && Map.isMap(value) && Map.isMap(value.get('appointment'))) {
      bookingStageDispatch({
        type:   BookingStageStateAction.SetStage,
        target: BookingStage.ExistingAppointmentSetup,
      });
    } else {
      bookingStageDispatch({
        type:   BookingStageStateAction.SetStage,
        target: BookingStage.NewAppointmentValidation,
      });
    }
  };

  const resolveValidateDatasourceAvailabilityAction = () => {
    bookingStageDispatch({
      type:             BookingStageStateAction.CompleteStageAction,
      targetActionType: BookingStageAction.ValidateDatasourceAvailability,
    });

    const isSupportedDatasource = Object.values(BookingDatasource).includes(datasource);
    let isDatasourceAvailable = false;

    if (datasource === BookingDatasource.RecruitmentCenter) {
      isDatasourceAvailable = campaign?.recruitmentCenter !== undefined;
    } else if (datasource === BookingDatasource.AssignedRecruiterCalendar) {
      // Recruiter availability is resolved by the backend round-robin pool.
      // An application id is still required to create the appointment.
      isDatasourceAvailable = application?.id != null;
    }

    if (isDatasourceAvailable && isSupportedDatasource) {
      bookingStageDispatch({
        type:   BookingStageStateAction.SetStage,
        target: BookingStage.NewAppointmentSetup,
      });
    } else {
      setLoadingError(LoadingError.BookingUnavailable);
      bookingStageDispatch({
        type:   BookingStageStateAction.SetStage,
        target: BookingStage.NewAppointmentForbiddenError,
      });
    }
  };

  const resolveSetSlotDurationAction = () => {
    bookingStageDispatch({
      type:             BookingStageStateAction.CompleteStageAction,
      targetActionType: BookingStageAction.SetSlotDuration,
    });

    let extractedSlotDuration;

    if (datasource === BookingDatasource.RecruitmentCenter) {
      extractedSlotDuration = campaign?.recruitmentCenter?.slotDuration;
    }
    // OLD (AssignedRecruiterCalendar): used the single assigned manager's calendar slotDuration.
    // Now the pool's slot duration is determined by the backend (primary record's calendar).
    // else if (datasource === BookingDatasource.AssignedRecruiterCalendar) {
    //   extractedSlotDuration = application?.assignedUser?.getIn(['calendars', 'collection', '0', 'slotDuration'], undefined);
    // }

    setSlotDuration(extractedSlotDuration ?? DEFAULT_BOOKING_SLOT_DURATION);
  };

  const resolveSetTimeZoneAction = () => {
    bookingStageDispatch({
      type:             BookingStageStateAction.CompleteStageAction,
      targetActionType: BookingStageAction.SetTimeZone,
    });

    if (!timeZone) {
      const defaultTimezone = getDefaultTimezone(timeZonesData, campaign);
      setTimeZone(defaultTimezone);
    }
  };

  const resolveFetchCalendarDataAction = () => {
    bookingStageDispatch({
      type:             BookingStageStateAction.ClaimStageAction,
      targetActionType: BookingStageAction.FetchCalendarData,
    });

    const windowExtensionDays = (
      appliedBookingWindowExtensions * DEFAULT_BOOKING_WINDOW_EXTENSION_DAYS
    );

    // Pool flow: backend owns each manager's booking window and minimum notice.
    // EOS only sends extensionDays so the backend can extend all windows uniformly.
    if (datasource === BookingDatasource.AssignedRecruiterCalendar) {
      fetchCalendarSlotsData({
        variables: {
          source:        datasourceToCalendarTypeMap[datasource],
          sort:          { field: 'timeframe', direction: 'ASC' },
          limit:         500,
          extensionDays: windowExtensionDays,
        },
        fetchPolicy:     'network-only',
        nextFetchPolicy: 'network-only',
      });
      return;
    }

    // RecruitmentCenter flow: EOS sends the timeframe (unchanged).
    let windowDays = campaign?.recruitmentCenter?.bookingWindowDays;
    let minimumBookingNoticeValue = campaign?.recruitmentCenter?.minimumBookingNotice?.value;
    let minimumBookingNoticeUnit = campaign?.recruitmentCenter?.minimumBookingNotice?.unit;

    windowDays = windowDays ?? DEFAULT_BOOKING_WINDOW_DAYS;
    minimumBookingNoticeValue = minimumBookingNoticeValue ?? DEFAULT_MINIMUM_BOOKING_NOTICE_VALUE;
    minimumBookingNoticeUnit = Object.values(BookingNoticeUnit).includes(minimumBookingNoticeUnit)
      ? minimumBookingNoticeUnit
      : BookingNoticeUnit.Minutes;

    const minimumBookingDate = DateTime.utc()
      .plus({ [minimumBookingNoticeUnit]: minimumBookingNoticeValue })
      .toISO();

    const maximumBookingDate = DateTime
      .utc().plus({ days: windowDays + windowExtensionDays }).toISO();

    fetchCalendarSlotsData({
      variables: {
        source: datasourceToCalendarTypeMap[datasource],
        search: {
          where: [
            {
              field: 'timeframe', value: [minimumBookingDate, maximumBookingDate], operator: 'BETWEEN', options: { data_type: 'tsrange' },
            },
            { field: 'slots', value: ['0'], operator: 'GT' },
          ],
        },
        sort:  { field: 'timeframe', direction: 'ASC' },
        limit: 500,
      },
      fetchPolicy:     'network-only',
      nextFetchPolicy: 'network-only',
    });
  };

  const resolveBuildCalendarAvailabilityAction = () => {
    const isFetchCalendarDataInProgress = pendingStageActions.some(
      (otherStageAction) => otherStageAction.get('type') === BookingStageAction.FetchCalendarData,
    );

    if (!isFetchCalendarDataInProgress) {
      bookingStageDispatch({
        type:             BookingStageStateAction.CompleteStageAction,
        targetActionType: BookingStageAction.BuildCalendarAvailability,
      });

      setCalendarAvailability(buildCalendarAvailability(rawSlots, timeZone, locale));

      // For pool bookings, derive slot duration from the actual slot data returned by the backend.
      // This ensures the displayed duration matches the primary record's configured slot_duration.
      if (datasource === BookingDatasource.AssignedRecruiterCalendar && rawSlots?.size > 0) {
        const firstSlot = rawSlots.find((s) => s.get('available'));
        if (firstSlot) {
          const start = DateTime.fromISO(firstSlot.get('startTime'));
          const end = DateTime.fromISO(firstSlot.get('endTime'));
          const derivedDuration = end.diff(start, 'minutes').minutes;
          if (derivedDuration > 0) {
            setSlotDuration(derivedDuration);
          }
        }
      }
    }
  };

  const resolveValidateCalendarAvailabilityAction = () => {
    const isBuildCalendarAvailabilityInProgress = pendingStageActions.some(
      (otherStageAction) => otherStageAction.get('type') === BookingStageAction.BuildCalendarAvailability,
    );

    if (!isBuildCalendarAvailabilityInProgress) {
      bookingStageDispatch({
        type:             BookingStageStateAction.CompleteStageAction,
        targetActionType: BookingStageAction.ValidateCalendarAvailability,
      });

      const calendarAvailabilitySlots = calendarAvailability
        .flatMap((dayAvailability) => dayAvailability.get('slots'))
        .filter((slot) => slot.get('available'));

      if (
        calendarAvailabilitySlots.size >= MIN_AVAILABLE_CALENDAR_SLOTS
        || (
          calendarAvailabilitySlots.size > 0
          && appliedBookingWindowExtensions >= MAX_BOOKING_WINDOW_EXTENSIONS
        )
      ) {
        bookingStageDispatch({
          type:   BookingStageStateAction.SetStage,
          target: BookingStage.NewAppointmentSelection,
        });

        if (selectedDay) {
          const selectedDayAvailability = calendarAvailability.find((dayAvailability) => dayAvailability.get('id') === selectedDay.get('id'));

          if (selectedDayAvailability) {
            setSelectedDay(selectedDayAvailability);
          } else {
            setSelectedDay(calendarAvailability.first());
          }
        } else {
          setSelectedDay(calendarAvailability.first());
        }

        setSelectedSlot(null);
      } else if (appliedBookingWindowExtensions < MAX_BOOKING_WINDOW_EXTENSIONS) {
        bookingStageDispatch({
          type:       BookingStageStateAction.AppendStageActions,
          newActions: new List([
            buildPendingStageAction(BookingStageAction.NotifyLimitedCalendarAvailability),
            buildPendingStageAction(BookingStageAction.FetchCalendarData),
            buildPendingStageAction(BookingStageAction.BuildCalendarAvailability),
            buildPendingStageAction(BookingStageAction.ValidateCalendarAvailability),
          ]),
        });

        setAppliedBookingWindowExtensions(appliedBookingWindowExtensions + 1);
      } else {
        setLoadingError(LoadingError.NoAvailableSlots);
        bookingStageDispatch({
          type:   BookingStageStateAction.SetStage,
          target: BookingStage.NewAppointmentForbiddenError,
        });
      }
    }
  };

  const resolveClearDaySelectionAction = () => {
    bookingStageDispatch({
      type:             BookingStageStateAction.CompleteStageAction,
      targetActionType: BookingStageAction.ClearDaySelection,
    });

    setSelectedDay(null);
    setSelectedSlot(null);
  };

  const resolveCreateAppointmentAction = () => {
    bookingStageDispatch({
      type:             BookingStageStateAction.ClaimStageAction,
      targetActionType: BookingStageAction.CreateAppointment,
    });

    onProcessing(name);
    setCreationError(null);

    // For the recruiter calendar pool, each slot carries the calendarId of the manager assigned
    // by round-robin. The backend creates the event on that calendar.
    const calendarId = datasource === BookingDatasource.RecruitmentCenter
      ? selectedSlot.get('id')
      : selectedSlot.get('calendarId');

    if (!calendarId) {
      bookingStageDispatch({
        type:   BookingStageStateAction.SetStage,
        target: BookingStage.NewAppointmentCreationError,
      });
      onProcessingCompleted(name);
      setCreationError('eos.landing_page.text.booking.appointment_creation_error');
      return;
    }

    createAppointment({
      variables: {
        appointments: [
          {
            applicationId: application.id,
            calendarId,
            calendarType:  datasourceToCalendarTypeMap[datasource],
            startTime:     selectedSlot.get('startTime').toISO(),
            endTime:       selectedSlot.get('endTime').toISO(),
            timezone:      timeZone,
          },
        ],
      },
    }).catch(() => {});
  };

  const resolveSetAppointmentAction = () => {
    const isAnyPrerequisiteInProgress = pendingStageActions.some(
      (otherStageAction) => [BookingStageAction.SetTimeZone, BookingStageAction.CreateAppointment].includes(otherStageAction.get('type')),
    );

    if (!isAnyPrerequisiteInProgress) {
      let patchedAppointment = null;

      if (currentStage === BookingStage.NewAppointmentCreation) {
        patchedAppointment = recordJS({ data: createAppointmentData }).getIn(['data', 'appointment', 'records', 'records', '0']);

        // Let's convert the appointment start and end times to DateTime objects
        // in the selected timezone and locate
        const startTime = DateTime.fromISO(patchedAppointment.get('startTime'), { zone: timeZone }).setLocale(locale);
        const endTime = DateTime.fromISO(patchedAppointment.get('endTime'), { zone: timeZone }).setLocale(locale);

        patchedAppointment = patchedAppointment
          .set('location', datasource === BookingDatasource.RecruitmentCenter ? campaign.recruitmentCenter.location : null)
          .set('startTime', startTime)
          .set('endTime', endTime);
      } else if (currentStage === BookingStage.ExistingAppointmentSetup) {
        patchedAppointment = value.get('appointment');
      }

      bookingStageDispatch({
        type:             BookingStageStateAction.CompleteStageAction,
        targetActionType: BookingStageAction.SetAppointment,
      });

      setAppointment(patchedAppointment);
    }
  };

  const resolveClearAppointmentAction = () => {
    bookingStageDispatch({
      type:             BookingStageStateAction.ClaimStageAction,
      targetActionType: BookingStageAction.ClearAppointment,
    });

    if (appointment) {
      onProcessing(name);
      destroyAppointment({ variables: { records: [appointment.get('id')] } });
      setAppointment(null);
    } else {
      bookingStageDispatch({
        type:             BookingStageStateAction.CompleteStageAction,
        targetActionType: BookingStageAction.ClearAppointment,
      });
    }
  };

  const resolveSubmitAnswerAction = () => {
    const isSetAppointmentInProgress = pendingStageActions.some(
      (otherStageAction) => otherStageAction.get('type') === BookingStageAction.SetAppointment,
    );

    if (forced || !isSetAppointmentInProgress) {
      bookingStageDispatch({
        type:             BookingStageStateAction.CompleteStageAction,
        targetActionType: BookingStageAction.SubmitAnswer,
      });

      if (forced && Map.isMap(value) && Map.isMap(value.get('appointment'))) {
        onChange(name, new Map({ forced: true }));
      } else if (!forced && appointment) {
        const formattedDate = appointment.get('startTime').toLocaleString(DEFAULT_SLOT_DATE_FORMAT_OPTIONS);
        const formattedStartTime = appointment.get('startTime').toLocaleString(DateTime.TIME_SIMPLE);
        const formattedEndTime = appointment.get('endTime').toLocaleString(DateTime.TIME_SIMPLE);
        const formattedTimeframe = `${formattedDate} ${formattedStartTime} - ${formattedEndTime} (${appointment.get('startTime').offsetNameShort})`;

        onChange(name, new Map({ text: formattedTimeframe, appointment }));
      } else if (!Map.isMap(value)) {
        onChange(
          name,
          new Map({ text: t('eos.landing_page.answer.booking.no_answer'), forced: true, incomplete: true }),
        );
      }
    }
  };

  const resolveClearSubmittedAnswerAction = () => {
    bookingStageDispatch({
      type:             BookingStageStateAction.CompleteStageAction,
      targetActionType: BookingStageAction.ClearSubmittedAnswer,
    });

    onChange(name, new Map({}));
  };

  const resolveDisplayBookingFormAction = () => {
    const isClearAppointmentInProgress = pendingStageActions.some(
      (otherStageAction) => otherStageAction.get('type') === BookingStageAction.ClearAppointment,
    );

    if (!isClearAppointmentInProgress) {
      bookingStageDispatch({
        type:             BookingStageStateAction.CompleteStageAction,
        targetActionType: BookingStageAction.DisplayBookingForm,
      });

      bookingStageDispatch({
        type:   BookingStageStateAction.SetStage,
        target: BookingStage.NewAppointmentValidation,
      });
    }
  };

  const resolveDisplayAppointmentAction = () => {
    const isSetAppointmentInProgress = pendingStageActions.some(
      (otherStageAction) => otherStageAction.get('type') === BookingStageAction.SetAppointment,
    );

    if (!isSetAppointmentInProgress) {
      bookingStageDispatch({
        type:             BookingStageStateAction.CompleteStageAction,
        targetActionType: BookingStageAction.DisplayAppointment,
      });

      bookingStageDispatch({
        type:   BookingStageStateAction.SetStage,
        target: BookingStage.ExistingAppointmentDisplay,
      });
    }
  };

  const resolveNotifyBookedSlotAction = () => {
    bookingStageDispatch({
      type:             BookingStageStateAction.CompleteStageAction,
      targetActionType: BookingStageAction.NotifyBookedSlot,
    });

    enqueueSnackbar(
      t('eos.landing_page.text.booking.errors.slot_booked.description'),
      {
        variant:          'info',
        autoHideDuration: DEFAULT_SNACKBAR_AUTO_HIDE_DURATION,
        anchorOrigin:     { vertical: 'bottom', horizontal: 'right' },
      },
    );
  };

  const resolveNotifyLimitedCalendarAvailability = () => {
    bookingStageDispatch({
      type:             BookingStageStateAction.CompleteStageAction,
      targetActionType: BookingStageAction.NotifyLimitedCalendarAvailability,
    });

    // Trigger the notification only on the first extension.
    if (appliedBookingWindowExtensions !== 1) return;

    if (datasource === BookingDatasource.AssignedRecruiterCalendar) {
      // Pool flow: notify all pool members using calendar record IDs from the slot query response.
      const poolIds = calendarSlotsData?.calendar?.slots?.poolCalendarIds;
      if (poolIds?.length > 0) {
        scheduleLimitedCalendarAvailabilityNotification({ variables: { records: poolIds } })
          .catch(() => {});
      }
    } else {
      const calendarId = application?.assignedUser?.getIn(['calendars', 'collection', '0', 'id'], null);
      if (calendarId) {
        scheduleLimitedCalendarAvailabilityNotification({ variables: { records: [calendarId] } })
          .catch(() => {});
      }
    }
  };

  const updateTimeZone = (_, newValue) => {
    bookingStageDispatch({
      type:       BookingStageStateAction.AppendStageActions,
      newActions: new List([
        buildPendingStageAction(BookingStageAction.BuildCalendarAvailability),
        buildPendingStageAction(BookingStageAction.ValidateCalendarAvailability),
      ]),
    });

    // Let's set the timezone in the local storage if available
    if (checkLocalStorage()) {
      setItem(BOOKING_TIMEZONE_STORAGE_KEY, newValue);
    }

    setTimeZone(newValue);
  };

  const retryNewAppointmentSetup = () => {
    bookingStageDispatch({
      type:       BookingStageStateAction.SetStageActions,
      newActions: defaultStageActionsMap.get(BookingStage.NewAppointmentSetup),
    });
  };

  const clearAppointment = () => {
    bookingStageDispatch({
      type:   BookingStageStateAction.SetStage,
      target: BookingStage.ExistingAppointmentDeletion,
    });
  };

  const clearSelectedSlot = () => {
    setSelectedSlot(null);
  };

  const confirmSlot = () => {
    bookingStageDispatch({
      type:   BookingStageStateAction.SetStage,
      target: BookingStage.NewAppointmentCreation,
    });
  };

  /* –– Effects
   * –––––––––––––––––––––––––––––––– */
  useEffect(() => {
    if (pendingStageActions.size > 0) {
      pendingStageActions
        .filter((stageAction) => stageAction.get('status') === BookingStageActionStatus.Pending)
        .forEach((stageAction) => {
          switch (stageAction.get('type')) {
            case BookingStageAction.SelectBookingMode: {
              resolveSelectBookingModeAction();
              break;
            }
            case BookingStageAction.ValidateDatasourceAvailability: {
              resolveValidateDatasourceAvailabilityAction();
              break;
            }
            case BookingStageAction.SetSlotDuration: {
              resolveSetSlotDurationAction();
              break;
            }
            case BookingStageAction.SetTimeZone: {
              resolveSetTimeZoneAction();
              break;
            }
            case BookingStageAction.FetchCalendarData: {
              resolveFetchCalendarDataAction();
              break;
            }
            case BookingStageAction.BuildCalendarAvailability: {
              resolveBuildCalendarAvailabilityAction();
              break;
            }
            case BookingStageAction.ValidateCalendarAvailability: {
              resolveValidateCalendarAvailabilityAction();
              break;
            }
            case BookingStageAction.ClearDaySelection: {
              resolveClearDaySelectionAction();
              break;
            }
            case BookingStageAction.CreateAppointment: {
              resolveCreateAppointmentAction();
              break;
            }
            case BookingStageAction.SetAppointment: {
              resolveSetAppointmentAction();
              break;
            }
            case BookingStageAction.ClearAppointment: {
              resolveClearAppointmentAction();
              break;
            }
            case BookingStageAction.SubmitAnswer: {
              resolveSubmitAnswerAction();
              break;
            }
            case BookingStageAction.ClearSubmittedAnswer: {
              resolveClearSubmittedAnswerAction();
              break;
            }
            case BookingStageAction.DisplayBookingForm: {
              resolveDisplayBookingFormAction();
              break;
            }
            case BookingStageAction.DisplayAppointment: {
              resolveDisplayAppointmentAction();
              break;
            }
            case BookingStageAction.NotifyBookedSlot: {
              resolveNotifyBookedSlotAction();
              break;
            }
            case BookingStageAction.NotifyLimitedCalendarAvailability: {
              resolveNotifyLimitedCalendarAvailability();
              break;
            }
            default: {
              throw new Error(`Unknown booking stage action: ${stageAction.get('type')}`);
            }
          }
        });
    }
  }, [pendingStageActions]);

  useEffect(() => {
    if (calendarSlotsData) {
      const immutableCalendarSlotsData = recordJS({ data: calendarSlotsData });
      setRawSlots(immutableCalendarSlotsData.getIn(['data', 'calendar', 'slots', 'collection']));
      setFailedCalendarSlotsLoadAttempts(0);

      bookingStageDispatch({
        type:             BookingStageStateAction.CompleteStageAction,
        targetActionType: BookingStageAction.FetchCalendarData,
      });
    }

    if (calendarSlotsDataError) {
      if (failedCalendarSlotsLoadAttempts >= MAX_FAILED_CALENDAR_SLOTS_LOAD_ATTEMPTS) {
        bookingStageDispatch({
          type:   BookingStageStateAction.SetStage,
          target: BookingStage.NewAppointmentForbiddenError,
        });
        setLoadingError(LoadingError.BookingUnavailable);
      } else {
        bookingStageDispatch({
          type:   BookingStageStateAction.SetStage,
          target: BookingStage.NewAppointmentSetupError,
        });
        setLoadingError(LoadingError.FailedCalendarFetch);
      }

      setFailedCalendarSlotsLoadAttempts(failedCalendarSlotsLoadAttempts + 1);
    }
  }, [calendarSlotsData, calendarSlotsDataError]);

  useEffect(() => {
    if (createAppointmentData) {
      const immutableCreateAppointmentData = recordJS({ data: createAppointmentData });
      const appointmentErrors = immutableCreateAppointmentData.getIn(['data', 'appointment', 'records', 'errors']);
      const appointmentValidations = immutableCreateAppointmentData.getIn(['data', 'appointment', 'records', 'validations']);

      if (
        (Map.isMap(appointmentErrors) && appointmentErrors.size > 0)
        || (Map.isMap(appointmentValidations) && appointmentValidations.size > 0)
      ) {
        if (List.isList(appointmentValidations.getIn(['0', 'calendar']))) {
          bookingStageDispatch({
            type:   BookingStageStateAction.SetStage,
            target: BookingStage.NewAppointmentForbiddenCreationError,
          });
        } else {
          bookingStageDispatch({
            type:   BookingStageStateAction.SetStage,
            target: BookingStage.NewAppointmentCreationError,
          });

          setCreationError(appointmentErrors.getIn(['0', '0']) || 'Unknown error');
        }
      } else {
        bookingStageDispatch({
          type:             BookingStageStateAction.CompleteStageAction,
          targetActionType: BookingStageAction.CreateAppointment,
        });
      }

      onProcessingCompleted(name);
    }

    if (createAppointmentError) {
      bookingStageDispatch({
        type:   BookingStageStateAction.SetStage,
        target: BookingStage.NewAppointmentCreationError,
      });

      onProcessingCompleted(name);
      setCreationError(createAppointmentError.message || 'Unknown error');
    }
  }, [createAppointmentData, createAppointmentError]);

  useEffect(() => {
    if (destroyAppointmentData || destroyAppointmentError) {
      bookingStageDispatch({
        type:             BookingStageStateAction.CompleteStageAction,
        targetActionType: BookingStageAction.ClearAppointment,
      });

      onProcessingCompleted(name);
    }
  }, [destroyAppointmentData, destroyAppointmentError]);

  useEffect(() => {
    if (Map.isMap(selectedDay) && selectedSlot) {
      setSelectedSlot(null);
    }
  }, [selectedDay]);

  useEffect(() => {
    if (!forced) return;

    bookingStageDispatch({
      type:   BookingStageStateAction.SetStage,
      target: BookingStage.ForcedAppointmentSubmission,
    });
  }, [forced]);

  /* –– Render
   * –––––––––––––––––––––––––––––––– */
  const containerStyles = useMemo(() => ({
    border:       1,
    borderRadius: 2,
    borderColor:  'grey.400',
    mt:           2,
    minHeight:    currentStage === BookingStage.ExistingAppointmentDisplay ? 'auto' : '500px',
    overflow:     currentStage === BookingStage.ExistingAppointmentDisplay ? 'hidden' : 'visible',
    position:     'relative',
  }), [currentStage]);

  const blurredContentStyles = useMemo(() => {
    if (globalErrorStages.includes(currentStage)) {
      return {
        filter:        'blur(4px)',
        pointerEvents: 'none',
        transition:    'filter 0.3s ease, opacity 0.3s ease',
      };
    }

    return {};
  }, [currentStage]);

  if (globalLoadingStages.includes(currentStage)) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={containerStyles}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Box sx={{ position: 'relative', width: 72, height: 72 }}>
            <CircularProgressTrack
              size={72}
              sx={{ position: 'absolute', top: 0, left: 0 }}
              variant="determinate"
              value={100}
            />
            <RoundedCircularProgress
              size={72}
              sx={{ position: 'absolute', top: 0, left: 0 }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{ mt: 1, opacity: 0.65 }}
          >
            {t('eos.landing_page.text.booking.loading')}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (currentStage === BookingStage.ExistingAppointmentDisplay) {
    return (
      <Box sx={containerStyles}>
        <AppointmentCard
          appointment={appointment}
          onDiscard={clearAppointment}
          disableReschedule={disableReschedule}
          onAutoSubmit={() => onChange(name, new Map({ forced: true }))}
        />
      </Box>
    );
  }

  return (
    <Box sx={containerStyles}>
      {globalErrorStages.includes(currentStage) && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            position:        'absolute',
            top:             0,
            left:            0,
            width:           '100%',
            height:          '100%',
            padding:         1.5,
            backgroundColor: '#192C390D',
            zIndex:          theme.zIndex.modal,
          }}
        >
          <Box sx={{
            width:           { xs: '100%', sm: '70%', md: '50%' },
            borderRadius:    2,
            backgroundColor: darken(theme.palette.background.paper, 0.005),
            p:               2.5,
            textAlign:       'center',
          }}
          >
            <Typography
              component="h4"
              variant="h4"
              sx={{ fontWeight: 'medium', lineHeight: 1.4 }}
            >
              {t(`eos.landing_page.text.booking.errors.${loadingError}.title`)}
            </Typography>
            <Typography
              component="p"
              variant="body2"
              color="text.secondary"
              mt={0.5}
            >
              {t(`eos.landing_page.text.booking.errors.${loadingError}.description`)}
            </Typography>
            {loadingError === LoadingError.FailedCalendarFetch && (
              <LoadingButton
                variant="contained"
                color="grey"
                loading={!!calendarSlotsDataLoading}
                onClick={retryNewAppointmentSetup}
                disableElevation
                disableRipple
                fullWidth
                startIcon={<AutorenewIcon />}
                sx={{
                  mt:                 2,
                  letterSpacing:      0,
                  backgroundColor:    'grey.300',
                  color:              'text.primary',
                  '&:hover, &:focus': {
                    backgroundColor: 'grey.400',
                  },
                  '&:active': {
                    backgroundColor: 'grey.500',
                  },
                  '&:disabled': {
                    backgroundColor: 'grey.100',
                  },
                  '&:disabled .MuiLoadingButton-loadingIndicator': {
                    color: 'grey.600',
                  },
                }}
              >
                {t(`eos.landing_page.text.booking.errors.${loadingError}.action`)}
              </LoadingButton>
            )}
          </Box>
        </Box>
      )}

      <Box
        pt={3}
        px={3}
        pb={4}
        sx={blurredContentStyles}
      >
        <Grid
          container
          alignItems="center"
        >
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: 'flex', alignItems: 'center', pr: 4 }}
          >
            <Typography
              variant="body"
              sx={{ fontWeight: 'medium', flexShrink: 0, flexGrow: 0 }}
            >
              {t('eos.landing_page.label.booking.timezone')}
            </Typography>
            :
            <Autocomplete
              id="timezone-autocomplete"
              value={timeZone}
              options={timeZonesData}
              disabled={disabled}
              onChange={updateTimeZone}
              getOptionLabel={(option) => option}
              renderOption={(props, option) => {
                const timeZonedDate = DateTime.now().setZone(option).setLocale(locale);

                return (
                  <Box
                    component="li"
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                    key={option}
                  >
                    <Box>
                      <Typography
                        variant="body"
                        component="p"
                        sx={{ fontWeight: 'medium' }}
                      >
                        {option}
                      </Typography>
                      <Typography
                        variant="caption"
                        component="p"
                      >
                        {timeZonedDate.offsetNameLong}
                        {' '}
                        (
                        {timeZonedDate.offsetNameShort}
                        )
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  size="small"
                />
              )}
              sx={{ ml: 1, flexGrow: 1, maxWidth: '250px' }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{ mt: { xs: 1, md: 0 } }}
          >
            <Typography
              variant="body"
              sx={{ fontWeight: 'medium' }}
            >
              {t('eos.landing_page.label.booking.duration')}
            </Typography>
            :
            <Typography
              variant="body"
              sx={{ ml: 1 }}
              data-testid={`slot-duration-${slotDuration}`}
            >
              {t('time.minutes', { minutes: slotDuration })}
            </Typography>
          </Grid>
        </Grid>

        <Grid container>
          <Grid
            item
            xs={12}
            md={6}
            sx={{ pr: 4 }}
          >
            <DatePicker
              calendarAvailability={calendarAvailability}
              selectedDate={selectedDay}
              onDateSelected={setSelectedDay}
              locale={locale}
              disabled={disabled || false}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            {!selectedSlot && (
              <AppointmentSlotPicker
                date={selectedDay}
                onSlotSelected={setSelectedSlot}
              />
            )}

            {selectedSlot && (
              <AppointmentSlotVerifier
                slot={selectedSlot}
                loading={!!createAppointmentLoading}
                disabled={disabled}
                onCancel={clearSelectedSlot}
                onConfirm={confirmSlot}
                error={creationError}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

/* ––
 * ––– Props declaration
 * –––––––––––––––––––––––––––––––– */
Booking.propTypes = {
  name:                  PropTypes.string,
  value:                 PropTypes.oneOfType([PropTypes.instanceOf(Map), PropTypes.oneOf([null])]),
  disabled:              PropTypes.bool,
  forced:                PropTypes.bool,
  onChange:              PropTypes.func,
  onProcessing:          PropTypes.func,
  onProcessingCompleted: PropTypes.func,
  datasource:            PropTypes.string,
  disableReschedule:     PropTypes.bool,
  application:           PropTypes.oneOfType([
    PropTypes.instanceOf(CandidateApplication),
    PropTypes.oneOf([null]),
  ]),
};

/* ––
 * ––– Exports
 * –––––––––––––––––––––––––––––––– */
export default Booking;
